// What the Plan picker shows (redesign slice 5c; wireframes 4 and 5): the
// Recipes tab's filters, Favorites and Recently cooked, the Leftovers tab's
// rows, and the one-tap leftovers suggestion on an empty meal. Pure
// functions, tested in tests/picker-view.test.mjs.
import { DAYS, DINING_OUT, MEAL_SLOTS, SEASONS } from "./constants";
import { normalizeDishes } from "./planUtils";
import { formatMinutes } from "./utils";
import { drawsOn, isCooked, mealDate, mealKey, mealLabel, sourceCandidates, type MealRef, type SourceCandidate } from "./planScaling";
import type { Day, DishType, MealSlot, PlansByWeek, Recipe, RecipeById } from "./types";

const DAY_MS = 86400000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const daysBetween = (earlier: Date, later: Date) => Math.round((startOfDay(later).getTime() - startOfDay(earlier).getTime()) / DAY_MS);
const capitalize = (s: string) => s.replace(/^./, (ch) => ch.toUpperCase());

// ------------------------------------------------------- recipes tab ---

const totalMinutes = (r: Pick<Recipe, "activeTime" | "cookTime">) => (r.activeTime || 0) + (r.cookTime || 0);

// The season a date falls in, as the recipe tags spell it (northern
// hemisphere, by month: Mar-May Spring, Jun-Aug Summer, Sep-Nov Fall).
export function seasonOf(date: Date): string {
  const m = date.getMonth();
  return SEASONS[m >= 2 && m <= 4 ? 0 : m >= 5 && m <= 7 ? 1 : m >= 8 && m <= 10 ? 2 : 3];
}

// "45 min · serves 4"
export function recipeMeta(r: Recipe): string {
  const minutes = totalMinutes(r);
  return [minutes ? formatMinutes(minutes) : "", r.servings ? `serves ${r.servings}` : ""].filter(Boolean).join(" · ");
}

export interface RecipeFilters {
  query: string;
  quick: boolean; // under 30 minutes in all
  inSeason: boolean; // tagged with the meal's season
  favorites: boolean;
}

export const NO_FILTERS: RecipeFilters = { query: "", quick: false, inSeason: false, favorites: false };

export const isFiltering = (f: RecipeFilters) => !!f.query.trim() || f.quick || f.inSeason || f.favorites;

// The recipes of one dish type that pass the filters, by title. `mealDay`
// decides the season.
export function filterRecipes(recipes: Recipe[], dishType: DishType, filters: RecipeFilters, mealDay: Date, isFavorite: (r: Recipe) => boolean): Recipe[] {
  const season = seasonOf(mealDay);
  return searchRecipes(recipes, filters.query).filter(
    (r) =>
      r.recipeType === dishType &&
      (!filters.quick || (totalMinutes(r) > 0 && totalMinutes(r) <= 30)) &&
      (!filters.inSeason || (r.seasons || []).includes(season)) &&
      (!filters.favorites || isFavorite(r))
  );
}

// Any recipe whose title matches, by title ("Something older").
export function searchRecipes(recipes: Recipe[], query: string): Recipe[] {
  const q = query.trim().toLowerCase();
  return recipes
    .filter((r) => !q || (r.title || "").toLowerCase().includes(q))
    .sort((a, b) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true }));
}

export interface RecentRecipe {
  recipe: Recipe;
  when: string; // "Today", "Yesterday", "On Mon", "Last week", "3 weeks ago"
}

// "On Mon" within the last week, then by weeks.
export function cookedWhen(date: Date, now: Date): string {
  const diff = daysBetween(date, now);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `On ${date.toLocaleDateString(undefined, { weekday: "short" })}`;
  if (diff < 14) return "Last week";
  return `${Math.floor(diff / 7)} weeks ago`;
}

// Recipes of this dish type cooked (planned, not as leftovers) on or before
// today, newest first, each once. Looks back four weeks.
export function recentlyCooked(plansByWeek: PlansByWeek, dishType: DishType, recipeById: RecipeById, now: Date, limit = 5): RecentRecipe[] {
  const today = startOfDay(now);
  const earliest = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 28);
  const found: { id: string; date: Date; slot: number }[] = [];
  for (const [weekStart, week] of Object.entries(plansByWeek)) {
    for (const day of DAYS) {
      const date = mealDate(weekStart, day);
      if (date < earliest || date > today) continue;
      for (const slot of MEAL_SLOTS) {
        const id = (normalizeDishes(week.days?.[day]?.[slot]) as Partial<Record<DishType, string>>)[dishType];
        if (!id || id === DINING_OUT || week.leftovers?.[mealKey(day, slot, dishType)]) continue;
        found.push({ id, date, slot: MEAL_SLOTS.indexOf(slot) });
      }
    }
  }
  found.sort((a, b) => b.date.getTime() - a.date.getTime() || b.slot - a.slot);
  const seen = new Set<string>();
  const out: RecentRecipe[] = [];
  for (const f of found) {
    const recipe = recipeById(f.id);
    if (!recipe || seen.has(f.id)) continue;
    seen.add(f.id);
    out.push({ recipe, when: cookedWhen(f.date, now) });
    if (out.length === limit) break;
  }
  return out;
}

// ----------------------------------------------------- leftovers tab ---

export interface LeftoverRow {
  candidate: SourceCandidate;
  title: string;
  meal: string; // "Thursday's dinner", "last night's dinner"
  from: string; // "Thursday's dinner · 2 days before"
  usedOn: Day | null; // another leftovers slot already draws on it
  sameDay: boolean;
  cooked: boolean; // marked cooked (0058): linking won't scale it
}

const RELATIVE = /^(today|last night|tomorrow)'s /;

const sameRef = (a: MealRef, b: MealRef) => a.weekStart === b.weekStart && a.day === b.day && a.mealSlot === b.mealSlot && a.dishType === b.dishType;

// The dishes these leftovers could come from, newest first: earlier days
// within the reach, and earlier meals the same day.
export function leftoverRows(plansByWeek: PlansByWeek, target: MealRef, recipeById: RecipeById, now: Date): LeftoverRow[] {
  const targetDate = mealDate(target.weekStart, target.day);
  return sourceCandidates(plansByWeek, target)
    .filter((c) => recipeById(c.recipeId))
    .map((c) => {
      const daysBefore = daysBetween(c.date, targetDate);
      const used = drawsOn(plansByWeek, c).find((d) => !sameRef(d.ref, target));
      return {
        candidate: c,
        title: recipeById(c.recipeId)!.title,
        meal: mealLabel(c, now),
        // "Last night's dinner" and "today's lunch" say when already.
        from: capitalize(mealLabel(c, now)) + (daysBefore > 0 && !RELATIVE.test(mealLabel(c, now)) ? ` · ${daysBefore} day${daysBefore === 1 ? "" : "s"} before` : ""),
        usedOn: used ? used.ref.day : null,
        sameDay: daysBefore === 0,
        cooked: isCooked(plansByWeek, c),
      };
    });
}

// Where an empty meal's leftovers most likely come from: the newest Main
// nothing else draws on yet, from the meals that usually leave leftovers
// for this one (last night's dinner for lunch, an earlier breakfast for
// breakfast), within two days.
const LIKELY_SOURCES: Record<MealSlot, MealSlot[]> = { breakfast: ["breakfast"], lunch: ["dinner", "lunch"], dinner: ["dinner"] };

export function leftoverSuggestion(plansByWeek: PlansByWeek, target: Omit<MealRef, "dishType">, recipeById: RecipeById, now: Date): LeftoverRow | null {
  const ref: MealRef = { ...target, dishType: "entree" };
  return (
    leftoverRows(plansByWeek, ref, recipeById, now).find(
      (row) =>
        row.candidate.dishType === "entree" &&
        LIKELY_SOURCES[target.mealSlot].includes(row.candidate.mealSlot) &&
        daysBetween(row.candidate.date, mealDate(target.weekStart, target.day)) <= 2 &&
        !row.usedOn
    ) ?? null
  );
}
